import instance from "@store/middleware/api";

/**
 * The YouTube Licence certificate for a resolved claim.
 *
 * The document arrives already rendered as blocks — the same blocks the PDF is
 * drawn from — so the modal only has to know how a paragraph, a list, a table,
 * a details block and a verbatim block look. Wording lives in one versioned template
 * on the backend; nothing here restates it.
 */

export type LicenceBlock =
  | string
  | string[]
  | { h: string[]; r: string[][] }
  | { pre: string }
  /** A label with its own lines beneath — the parties, the track, the video. */
  | { label: string; lines: string[] };

export interface LicenceSection {
  title: string;
  blocks: LicenceBlock[];
}

export interface LicenceCertificate {
  licence: {
    id: string;
    licenceCode: string;
    basis: "free_use" | "single_licence" | "subscription";
    status: "active" | "void";
    voidReason: string | null;
    issuedAt: string;
    whitelistedAt: string | null;
  };
  rendered: {
    title: string;
    templateVersion: string;
    sections: LicenceSection[];
    text: string;
    summary: string;
  };
}

/** The generic YouTube Licence terms — no per-user data. Public; backs the
    /licence-terms page and the pre-purchase preview modal. */
export async function getGenericLicenceTerms(
  basis: "single_licence" | "subscription" | "free_use" = "single_licence",
): Promise<LicenceCertificate["rendered"]> {
  const { data } = await instance.get<{ rendered: LicenceCertificate["rendered"] }>("/licenses/terms", {
    params: { basis },
  });
  return data.rendered;
}

/**
 * Turn the raw generic template into the form shown to a buyer BEFORE purchase
 * (the public /licence-terms page and the pre-purchase preview modal): drop the
 * per-licence "This Licence" facts section — on the generic terms it only holds
 * placeholder descriptions of what a real certificate fills in — and renumber
 * the remaining sections so the terms still read 1, 2, 3… Shared so the page and
 * the modal can never diverge.
 */
export function toPublicLicenceSections(sections: LicenceSection[]): LicenceSection[] {
  let n = 0;
  return sections
    .filter((s) => !/this licence/i.test(s.title))
    .map((s) => {
      n += 1;
      return { ...s, title: s.title.replace(/^\s*\d+\.\s*/, `${n}. `) };
    });
}

export async function getClaimCertificate(claimRowId: string): Promise<LicenceCertificate> {
  const { data } = await instance.get<LicenceCertificate>(`/licenses/certificate/claim/${claimRowId}`);
  return data;
}

/**
 * Open the certificate PDF in a new tab (S-005). The blank tab is opened
 * synchronously, inside the click gesture, so popup blockers don't kill it;
 * the short-lived signed token follows. Mirrors `utils/invoicePdf.ts` — a new
 * tab can't carry an Authorization header, so ownership is proven on the mint
 * call and the tab carries the signature.
 */
export async function openLicencePdfTab(claimRowId: string): Promise<void> {
  const win = window.open("", "_blank");
  try {
    const resp = await instance.get(`/licenses/certificate/claim/${claimRowId}/pdf-token`);
    const token = resp.data?.token as string | undefined;
    if (!token) throw new Error("no token");
    const baseUrl = instance.defaults.baseURL || process.env.REACT_APP_API_URL || "";
    const url = `${baseUrl}/licenses/certificate/claim/${claimRowId}/pdf?token=${encodeURIComponent(token)}`;
    if (win) win.location.href = url;
    else window.open(url, "_blank");
  } catch {
    if (win) win.close();
  }
}
